import { useMemo } from "react";
import { ABSOLUTE_MAX_LEVERAGE } from "@/solana/constants";
import type { Product } from "./product-context";
import { useMarkets as useDynamicMarkets } from "@/domains/markets";
import { useMarkets as useLegacyMarkets } from "@/solana/hooks/use-markets";
import { useMarketStats } from "@/backend/hooks/use-market-stats";

export type ProductWithMarketData = Product & {
  lastPrice?: number;
  priceChange24h?: number;
  priceChangePercent24h?: number;
  volume24h?: number;
  // Additional fields for market selector
  fundingRate?: number;
  openInterestUsd?: number;
  maxLeverage?: number;
};

export const useProductOptions = () => {
  const { products, isLoading: isLoadingDynamic } = useDynamicMarkets();
  const { markets: legacyMarkets, isLoading: isLoadingLegacy } = useLegacyMarkets();
  const { stats: marketStats } = useMarketStats();

  // Enrich products with market data and stats
  const enrichedProducts = useMemo(() => {
    if (!legacyMarkets || legacyMarkets.length === 0) {
      return products;
    }

    return products.map((product): ProductWithMarketData => {
      // Find matching market data for perpetual products
      const marketData = legacyMarkets.find((m) => m.symbol === product.id);
      // Find matching stats from WebSocket
      const stats = marketStats.find((s) => s.market === product.id);

      if (marketData) {
        return {
          ...product,
          lastPrice: marketData.currentPrice,
          priceChange24h: marketData.priceChange24h,
          priceChangePercent24h: marketData.priceChangePercent24h,
          volume24h: marketData.volume24h,
          // Add stats data if available
          fundingRate: stats ? parseFloat(stats.funding_rate) : marketData.fundingRate,
          openInterestUsd: stats ? parseFloat(stats.open_interest_usd) : undefined,
          maxLeverage: stats?.max_leverage ?? ABSOLUTE_MAX_LEVERAGE,
        };
      }

      return product;
    });
  }, [products, legacyMarkets, marketStats]);

  return useMemo(() => {
    return { 
      products: enrichedProducts, 
      isLoading: isLoadingDynamic || isLoadingLegacy
    };
  }, [enrichedProducts, isLoadingDynamic, isLoadingLegacy]);
};
